/* eslint-disable angular/document-service */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';

function ContactsModal({
    isOpened, onCloseDialog, confirmContact, initContact, openEditGuest, updateContact = false,
}) {
    const [contacts, setContacts] = useState([]);
    const [selected, setSelected] = useState(initContact);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [additionalContacts, setAdditionalContacts] = useState(false);

    // Find the meta tag with name="csrf-token"
    const csrfMetaTag = document.querySelector('meta[name="csrf-token"]');
    // Extract the content attribute value (which is the CSRF token)
    const csrfToken = csrfMetaTag ? csrfMetaTag.getAttribute('content') : null;

    const handleSearchChange = (e) => {
        fetchContacts(e.target.value);
    };

    const handleSelectContact = (e) => {
        if (!e.donor_id) return;
        if (selected) selected.selected = false;
        selectContact(e.id);
    };

    const selectContact = (newContactId) => {
        if (!contacts) return;
        const temp = contacts.find((x) => x.id === newContactId);
        if (temp) temp.selected = true;
        setSelected(temp);
    };

    const fetchContacts = (search, isExclude = true) => {
        try {
            const params = {};

            if (search) {
                // search resets the list so should not exclude contacts
                params.search = search;
                setAdditionalContacts(null);
            } else if (additionalContacts && isExclude) {
                // get an array of contact ids already loaded
                params.excludeIds = contacts.map(function (contact) {
                    return contact.id;
                });
            }

            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,

                },
                body: JSON.stringify(params),
            };

            setLoadingContacts(true);

            fetch('/api/project/missingContacts', options)
                .then((response) => response.json())
                .then((data) => {
                    setLoadingContacts(false);
                    let tempContacts;
                    if (params.excludeIds) {
                        tempContacts = contacts.concat(data.data.contacts);
                    } else {
                        tempContacts = data.data.contacts;
                    }
                    const tempSelected = tempContacts.find((x) => x.id === initContact);
                    if (tempSelected) tempSelected.selected = true;
                    setSelected(tempSelected);
                    setContacts(tempContacts);
                    setAdditionalContacts(tempContacts.length === data.data.numPerPage);
                })
                .catch((error) => console.log('ERROR: ', error));
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const handleScroll = (e) => {
        if (!additionalContacts || loadingContacts) return;
        const bottom = Math.abs(e.target.scrollHeight - (e.target.scrollTop + e.target.clientHeight)) <= 1;
        if (bottom) {
            fetchContacts();
        }
    };

    const handleClose = () => {
        // reset to selected contact if user closes out of the modal
        if (selected && selected.id !== initContact) {
            selected.selected = false;
            selectContact(initContact);
        }
        onCloseDialog();
    };

    const handleConfirm = () => {
        confirmContact(selected);
    };

    useEffect(() => {
        if (!initContact && selected) {
            selected.selected = false;
            setSelected(null);
        }
    }, [initContact]);

    useEffect(() => {
        fetchContacts();
    }, []);

    useEffect(() => {
        if (updateContact) {
            fetchContacts('', false);
        }
    }, [updateContact]);

    return (
        isOpened && (
            <div id='popupContainer' className='tv-overlay overlay-form pv-contact-overlay overlay-guest-add'>
                <div
                    id='backdrop'
                    className='tv-overlay__backdrop'
                    data-a11y-dialog-hide='true'
                ></div>
                <div className='tv-overlay__content-wrapper' role='document'>
                    <div className='tv-overlay__header'>
                        <button
                            className='btn btn--ghost btn--close'
                            onClick={handleClose}
                            aria-label='Close dialog'
                        >
                            <svg
                                className='tv-icon close'
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 24 24'
                                aria-hidden="true"
                                style={{
                                    width: 40, height: 40,
                                }}
                            >
                                <path
                                    d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/>
                                <path d='M0 0h24v24H0z' fill='none'/>
                            </svg>
                        </button>
                    </div>
                    <div className='tv-overlay__content guest-select'>
                        <h2 className="margin-b-8px font-size-24px">Add from Contacts</h2>
                        <p className="font-size-16px margin-t-8px margin-b-8px">
                            Select contacts below to add them to your campaign.
                        </p>
                        <div
                            className="control-area flex align-items-center justify-content-space-around margin-b-8px">
                            <div className="search-field">
                                <form>
                                    <label htmlFor="guest-add-dialog-contact-search" className="sr-only">Search for
                                        a Contact</label>
                                    <i className="fa fa-search"></i>
                                    <input id="guest-add-dialog-contact-search"
                                        placeholder="Search for a Contact"
                                        onChange={handleSearchChange}/>
                                </form>
                            </div>
                        </div>
                        <div id="pv-contacts-list" className="guest-select-list margin-center" onScroll={handleScroll} data-testid="pv-contacts-list">
                            { contacts.map((contact) => (
                                <div className={`guest-item text-align-left ${contact.selected ? 'selected' : ''}`} key={contact.id} onClick={() => handleSelectContact(contact)}>
                                    <span
                                        className={`contact-name contact-name-${contact.id} font-size-16px inline-block width-75 text-overflow-ellipsis v-align-middle`}
                                        style={{ opacity: contact.donor_id ? 1 : 0.4 }}
                                        onClick={(e) => (contact.donor_id ? '' : openEditGuest(e, contact.id))}>{contact.name + ' ' + contact.last_name}
                                    </span>
                                    <Tooltip anchorSelect={`.contact-name-${contact.id}`} style={{ fontSize: '14px', maxWidth: '340px', whiteSpace: 'break-spaces' }} place='top-start' variant='light'>
                                        {contact.name + ' ' + contact.last_name}
                                    </Tooltip>
                                    <span
                                        className={`contact-email contact-email-${contact.id} hide-500 font-size-16px inline-block width-75 text-overflow-ellipsis v-align-middle`}
                                        style={{ color: contact.donor_id ? 'inherit' : '#939393' }}>
                                        {contact.donor_id ? contact.email : 'No Donor ID Found'}
                                        {
                                            !contact.donor_id && <i className={`fa fa-exclamation-circle exclamation-${contact.id}`} style={{ marginLeft: 5, color: '#939393' }}></i>
                                        }
                                    </span>
                                    <Tooltip anchorSelect={contact.donor_id ? `.contact-email-${contact.id}` : `.exclamation-${contact.id}`} style={{ fontSize: '14px', maxWidth: '340px', whiteSpace: 'break-spaces' }} place='top-start' variant='light'>
                                        {contact.donor_id ? contact.email : 'Please add an email to this contact by clicking “view” and then using the edit recipient button in the top right corner of the panel that appears'}
                                    </Tooltip>
                                    <span className="contact-view">
                                        <button className={`btn ${contact.donor_id ? 'btn--primary-alt' : 'btn--secondary'}`} onClick={(e) => openEditGuest(e, contact.id)}>
                                            <i className="fas fa-user-edit margin-r-4px"></i>
                                            View
                                        </button>
                                    </span>
                                </div>
                            ))}

                            {loadingContacts && (
                                <div className="guest-item guest-loading text-align-left">
                                    <i className="fa fa-spinner fa-pulse"></i>
                                </div>
                            )}
                        </div>
                        <div className='tv-overlay__message text-align-right'>
                            <div
                                id='overlay-message-buttons'
                                className='tv-overlay__action'
                            >
                                <button
                                    type='button'
                                    className='btn btn--primary'
                                    onClick={handleConfirm}
                                    disabled={selected ? selected.id === initContact : false}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ));
}

ContactsModal.propTypes = {
    isOpened: PropTypes.bool,
    onCloseDialog: PropTypes.func,
    confirmContact: PropTypes.func,
    initContact: PropTypes.number,
    updateContact: PropTypes.bool,
    openEditGuest: PropTypes.func,
};

export default ContactsModal;
